import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { ArrowBackIos } from "@mui/icons-material";
import { BackArrowStyle } from "services/constants";
import { useSelector } from "react-redux";
import { FilterProvider } from "pages/FilterContext";
import { CustomFilter, withFreshGeneratedOn } from "components/CustomComponents/CustomFilter";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import {
  GlobalBreadcrumb,
  globalTextFieldSx,
  sharedStickyFooterBarSx,
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  APP_BAR_OFFSET,
  StatCard,
  designColors,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
  withDesignFont,
} from "services/constants/designTokens";
import { useFilteredRows, useMeasuredHeight } from "pages/PayRoll/Salary/Shared/salaryUi";
import { SelectField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { useHoistedBreadcrumb } from "./settingsBreadcrumb";

// Shared building blocks for the Payroll statutory screens (PF first, then
// ESI / PT): Configuration > Statutory, Statutory > <item> and Reports >
// Statutory Reports. Everything is made from the app's existing parts
// (StatCard, CustomFilter, ApproveTable, GlobalSidePanel ...) so these
// screens look like the Pay Inputs and Salary lists.

// Card colours, picked by name so every statutory screen uses the same set.
export const CARD_TONES = {
  blue: { accent: "#2878FF", tint: "#EAF2FF" },
  green: { accent: "#12B76A", tint: "#E7F8EF" },
  purple: { accent: "#6D5DF6", tint: "#F1EDFF" },
  pink: { accent: "#DB2777", tint: "#FDF2F8" },
  amber: { accent: "#F0954A", tint: "#FFF4E5" },
  red: { accent: "#DC2626", tint: "#FEF2F2" },
  teal: { accent: designColors.tealDark, tint: designColors.tealTint },
  slate: { accent: designColors.slate600, tint: designColors.slate100 },
};

// Page: breadcrumb, title, subtitle, an optional block on the right (e.g.
// the active PF version), then the body.
// onBack: shows a back arrow before the title (e.g. back to the report list).
// switchAction: the settings ⚙ / "View …" switch (Shared/settingsSwitch.js),
// shown after aside.
export const StatutoryPage = ({ breadcrumb, title, subtitle, aside, switchAction, nav, onBack, children }) => {
  // Inside Payroll Settings the breadcrumb shows at the top of the screen.
  const breadcrumbHoisted = useHoistedBreadcrumb(breadcrumb);
  return (
  <ThemeProvider theme={withDesignFont}>
    <Mui.Box sx={{ ...designShell.pageSurface, pb: 2 }}>
      <Mui.Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: 2,
          px: 2,
          pt: 2,
          mb: 2,
        }}
      >
        <Mui.Box sx={{ minWidth: 0 }}>
          {breadcrumbHoisted ? null : <GlobalBreadcrumb trail={breadcrumb} />}
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            {onBack ? (
              <Mui.IconButton onClick={onBack} aria-label="Back" sx={BackArrowStyle}>
                <ArrowBackIos sx={{ fontSize: 18, ml: 0.5 }} />
              </Mui.IconButton>
            ) : null}
            <Mui.Typography component="h1" sx={designType.pageTitle}>
              {title}
            </Mui.Typography>
          </Mui.Stack>
          {subtitle ? <Mui.Typography sx={designType.pageSubtitle}>{subtitle}</Mui.Typography> : null}
          {nav}
        </Mui.Box>
        {switchAction ? (
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "center" }}>
            {aside}
            {switchAction}
          </Mui.Stack>
        ) : (
          aside
        )}
      </Mui.Box>
      <Mui.Box sx={{ px: 2 }}>{children}</Mui.Box>
    </Mui.Box>
  </ThemeProvider>
  );
};

// Page tabs in a white card (same look as the Salary / Pay Inputs tabs).
export const StatutoryTabs = ({ value, onChange, tabs }) => (
  <Mui.Box sx={{ ...designShell.sectionCard, px: 2, mb: 1.5 }}>
    <Mui.Tabs
      value={value}
      onChange={(event, next) => onChange(next)}
      variant="scrollable"
      allowScrollButtonsMobile
      sx={designShell.panelTabs}
    >
      {tabs.map((tab) => (
        <Mui.Tab key={tab.value} value={tab.value} label={tab.label} />
      ))}
    </Mui.Tabs>
  </Mui.Box>
);

// KPI cards. cards: [{ key, title, value, detail, icon, tone, filter }].
// With onChange the cards filter the table below (filter: false opts a
// card out, e.g. "Locations not linked").
export const StatCardGrid = ({ cards, value, onChange }) => (
  <Mui.Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "repeat(2, minmax(0, 1fr))",
        lg: `repeat(${cards.length}, minmax(0, 1fr))`,
      },
      gap: 1.5,
      mb: 2,
    }}
  >
    {cards.map((card) => {
      const tone = CARD_TONES[card.tone] || CARD_TONES.teal;
      const clickable = Boolean(onChange) && card.filter !== false;
      return (
        <StatCard
          key={card.key}
          icon={card.icon}
          title={card.title}
          value={card.value}
          detail={card.detail}
          accent={tone.accent}
          tint={tone.tint}
          onClick={clickable ? () => onChange(card.key) : undefined}
          isActive={clickable && value === card.key}
          activeBorder
        />
      );
    })}
  </Mui.Box>
);

// The toolbar's "+ Add" button (shown at the end of the CustomFilter bar).
export const ToolbarAddButton = ({ children, ...props }) => (
  <PrimaryButton sx={{ minWidth: 0, minHeight: 40, whiteSpace: "nowrap", flexShrink: 0 }} {...props}>
    {children}
  </PrimaryButton>
);

// Standard list: sticky CustomFilter toolbar (saved view, search, sort,
// filter, download), the sticky-header table and the pagination footer.
// headers: [{ name, key, type, icon, align }] (PayRollHeaders format).
// CustomFilter needs a FilterProvider; each list gets its own, so filters
// set on one tab don't carry over to another.
export const StatutoryList = (props) => {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  return (
    <FilterProvider domain={domain} userDetails={userDetails} subModuleId={props.subModuleId}>
      <StatutoryListBody {...props} />
    </FilterProvider>
  );
};

const StatutoryListBody = ({
  title,
  headers,
  titleOverride,
  rows,
  renderCell,
  onRowClick,
  rowKey = "id",
  itemLabel = "rows",
  searchPlaceholder,
  action,
  metadata,
  loading = false,
  rowSx,
  selectedKey,
  // Server mode (optional): `rows` is one page from the server. server = {
  // search, onSearchChange, page (0-based), rowsPerPage, count, onPageChange,
  // onRowsPerPageChange, exportRows: async () => every matching row,
  // downloadFile?: async (format) => true when it handled the download,
  // e.g. a server-made xlsx }.
  // Search, paging and export then go to the server; column filters and
  // sorting still apply to the loaded page.
  server,
  // Row checkboxes (optional): selection = { keys: [rowKey values], onChange(keys) }.
  // The header box ticks every row that matches the search / filters.
  selection,
}) => {
  const [toolbarRef, toolbarHeight] = useMeasuredHeight();
  const [footerRef, footerHeight] = useMeasuredHeight();
  const [visibleRows, onFilteredDataChange] = useFilteredRows(rows);
  const [localPage, setPage] = useState(0);
  const [localRowsPerPage, setRowsPerPage] = useState(25);
  const writers = useExcelDownload({ maxPdfRows: 5000 });
  const page = server ? server.page : localPage;
  const rowsPerPage = server ? server.rowsPerPage : localRowsPerPage;

  useEffect(() => {
    if (!server && page > 0 && page * rowsPerPage >= visibleRows.length) setPage(0);
  }, [server, page, rowsPerPage, visibleRows.length]);

  const pageRows = server ? visibleRows : visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  // Server mode exports every matching row, not only the loaded page.
  const exportAll = server?.exportRows
    ? async (_data, onProgress, format = "excel") => {
        if (server.downloadFile && (await server.downloadFile(format))) return;
        const all = await server.exportRows();
        const writer = format === "pdf" ? writers.downloadPdf : format === "csv" ? writers.downloadCsv : writers.downloadExcel;
        await writer(all, headers, title, {}, metadata ? withFreshGeneratedOn(metadata) : null, [], onProgress);
      }
    : undefined;
  const headCellSx = { ...sharedTableHeadCellSx, top: APP_BAR_OFFSET + toolbarHeight, whiteSpace: "nowrap" };
  const colSpan = (headers?.length || 1) + (selection ? 1 : 0);
  const selectedKeys = new Set((selection?.keys || []).map(String));
  const visibleKeys = selection ? visibleRows.map((row) => String(row[rowKey])) : [];
  const allTicked = visibleKeys.length > 0 && visibleKeys.every((key) => selectedKeys.has(key));
  const someTicked = visibleKeys.some((key) => selectedKeys.has(key));
  const tickAll = () => {
    const next = new Set(selectedKeys);
    visibleKeys.forEach((key) => (allTicked ? next.delete(key) : next.add(key)));
    selection.onChange([...next]);
  };
  const tickRow = (key) => {
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    selection.onChange([...next]);
  };
  const checkboxSx = { p: 0.5, "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: designColors.tealDark } };

  return (
    <>
      <Mui.Box ref={toolbarRef} sx={{ position: "sticky", top: APP_BAR_OFFSET, zIndex: 4, backgroundColor: designColors.white }}>
        <CustomFilter
          title={title}
          titleOverride={titleOverride}
          filterHeaders={headers}
          data={rows}
          filteredData={visibleRows}
          onFilteredDataChange={onFilteredDataChange}
          screenAccess={true}
          hideCount={true}
          downloadFormats={["excel", "pdf", "csv"]}
          includeMetadata={Boolean(metadata)}
          metadata={metadata || {}}
          labeledToolbar={true}
          searchPlaceholder={searchPlaceholder}
          newButtonComponent={action}
          searchValue={server?.search}
          onSearchChange={server?.onSearchChange}
          customDownloadHandler={exportAll}
        />
      </Mui.Box>
      <Mui.Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
        <Mui.TableContainer sx={{ width: "100%", overflowX: "visible", pb: footerHeight ? `${footerHeight}px` : 0 }}>
          <ApproveTable stickyHeader>
            <Mui.TableHead sx={designShell.tableStickyHead}>
              <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                {selection ? (
                  <Mui.TableCell padding="checkbox" sx={{ ...headCellSx, width: 48 }}>
                    <Mui.Checkbox
                      size="small"
                      checked={allTicked}
                      indeterminate={someTicked && !allTicked}
                      onChange={tickAll}
                      inputProps={{ "aria-label": "Select all rows" }}
                      sx={checkboxSx}
                    />
                  </Mui.TableCell>
                ) : null}
                {headers.map((head) => (
                  <Mui.TableCell key={head.key} align={head.align} sx={headCellSx}>
                    {head.name}
                  </Mui.TableCell>
                ))}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {loading ? (
                <Mui.TableRow>
                  {/* Same loader as the Pay Inputs lists: "Loading...", 40vh, no row border. */}
                  <Mui.TableCell colSpan={colSpan} sx={{ borderBottom: "none" }}>
                    <Loader info="Loading..." minHeight="40vh" />
                  </Mui.TableCell>
                </Mui.TableRow>
              ) : visibleRows.length === 0 ? (
                <TableEmptyState dataLength={rows} colSpan={colSpan} />
              ) : (
                pageRows.map((row) => (
                  <Mui.TableRow
                    key={row[rowKey]}
                    hover={Boolean(onRowClick)}
                    selected={selectedKey != null && String(selectedKey) === String(row[rowKey])}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{
                      cursor: onRowClick ? "pointer" : "default",
                      ...designShell.tableRowHover,
                      "&:last-child td": { borderBottom: "none" },
                      ...(rowSx ? rowSx(row) : {}),
                    }}
                  >
                    {selection ? (
                      <Mui.TableCell padding="checkbox" sx={{ ...sharedTableBodyCellSx, py: 0.5 }} onClick={(event) => event.stopPropagation()}>
                        <Mui.Checkbox
                          size="small"
                          checked={selectedKeys.has(String(row[rowKey]))}
                          onChange={() => tickRow(String(row[rowKey]))}
                          inputProps={{ "aria-label": `Select ${row.name || row[rowKey]}` }}
                          sx={checkboxSx}
                        />
                      </Mui.TableCell>
                    ) : null}
                    {headers.map((head) => (
                      <Mui.TableCell key={head.key} align={head.align} sx={{ ...sharedTableBodyCellSx, py: 1.25 }}>
                        {renderCell(row, head.key)}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                ))
              )}
            </Mui.TableBody>
          </ApproveTable>
        </Mui.TableContainer>
        <Mui.Box ref={footerRef} sx={sharedStickyFooterBarSx}>
          {paginationFooter({
            page,
            rowsPerPage,
            total: server ? server.count : visibleRows.length,
            itemLabel,
            onPageChange: server ? server.onPageChange : setPage,
            onRowsPerPageChange: (event) => {
              const size = parseInt(event.target.value, 10);
              if (server) {
                server.onRowsPerPageChange(size);
                return;
              }
              setRowsPerPage(size);
              setPage(0);
            },
            rowsPerPageOptions,
          })}
        </Mui.Box>
      </Mui.Box>
    </>
  );
};

// Header card at the top of a side panel (same look as the Salary
// Component / Pay Inputs view header): round avatar with initials or an
// icon, the name and a status chip, then a meta line.
export const RecordHeaderCard = ({ avatar, title, chip, meta, tone = "teal" }) => {
  const tones = {
    teal: { from: designColors.tealTint, avatarBg: designColors.tealDark },
    warning: { from: "#FFF4E5", avatarBg: "#F0954A" },
    danger: { from: "#FEF2F2", avatarBg: "#DC2626" },
    slate: { from: designColors.slate100, avatarBg: designColors.slate500 },
  };
  const colors = tones[tone] || tones.teal;
  return (
    <Mui.Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        border: `1px solid ${designColors.slate200}`,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${colors.from} 0%, ${designColors.white} 100%)`,
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
      }}
    >
      <Mui.Stack direction="row" spacing={2} alignItems="center">
        <Mui.Avatar sx={{ width: 52, height: 52, fontSize: 16, fontWeight: 700, backgroundColor: colors.avatarBg, color: designColors.white }}>
          {avatar}
        </Mui.Avatar>
        <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
          <Mui.Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
            <Mui.Typography sx={{ fontSize: { xs: 17, sm: 18 }, fontWeight: 700, color: designColors.slate900, lineHeight: 1.2 }}>
              {title}
            </Mui.Typography>
            {chip}
          </Mui.Stack>
          {meta ? <Mui.Typography sx={{ ...designType.helperText, mt: 0.75 }}>{meta}</Mui.Typography> : null}
        </Mui.Box>
      </Mui.Stack>
    </Mui.Box>
  );
};

// Teal on/off switch with its label (and an optional extra control, e.g.
// the VPF "max %" box) on one line.
export const SwitchLine = ({ checked, onChange, label, disabled, children }) => (
  <Mui.Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ minHeight: 40 }}>
    <Mui.Switch
      checked={Boolean(checked)}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      inputProps={{ "aria-label": typeof label === "string" ? label : undefined }}
      sx={{
        "& .MuiSwitch-switchBase.Mui-checked": { color: designColors.tealDark },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: designColors.teal },
      }}
    />
    <Mui.Typography sx={{ ...designType.tableBodyCell, color: disabled ? designColors.slate500 : designColors.slate900 }}>
      {label}
    </Mui.Typography>
    {children}
  </Mui.Stack>
);

// Plain number / text input with the app's text-field look, for the
// settings cards (value / onChange(value)).
export const PlainInput = ({ value, onChange, type = "text", error, disabled, placeholder, adornment, endAdornment, sx, inputProps }) => (
  <Mui.TextField
    size="small"
    type={type}
    value={value ?? ""}
    placeholder={placeholder}
    disabled={disabled}
    error={Boolean(error)}
    onChange={(event) => onChange(event.target.value)}
    InputProps={{
      startAdornment: adornment ? <Mui.InputAdornment position="start">{adornment}</Mui.InputAdornment> : undefined,
      endAdornment: endAdornment ? <Mui.InputAdornment position="end">{endAdornment}</Mui.InputAdornment> : undefined,
    }}
    inputProps={inputProps}
    sx={{ ...globalTextFieldSx, width: "100%", ...sx }}
  />
);

// Multi-select with removable chips (work locations, exit reasons).
// options: [{ value, label, disabled, hint }].
export const ChipMultiSelect = ({ value = [], onChange, options, placeholder, error, disabled }) => (
  <Mui.Autocomplete
    multiple
    size="small"
    disableCloseOnSelect
    disabled={disabled}
    options={options}
    value={options.filter((option) => value.includes(option.value))}
    isOptionEqualToValue={(option, selected) => option.value === selected.value}
    getOptionLabel={(option) => option.label}
    getOptionDisabled={(option) => Boolean(option.disabled)}
    onChange={(event, selected) => onChange(selected.map((option) => option.value))}
    renderOption={(props, option, { selected }) => (
      <li {...props} key={option.value}>
        <Mui.Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
        <Mui.Box sx={{ minWidth: 0 }}>
          <Mui.Typography sx={designType.tableBodyCell}>{option.label}</Mui.Typography>
          {option.hint ? <Mui.Typography sx={designType.captionText}>{option.hint}</Mui.Typography> : null}
        </Mui.Box>
      </li>
    )}
    renderTags={(selected, getTagProps) =>
      selected.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        return <Mui.Chip key={key} size="small" label={option.label} {...tagProps} sx={{ fontWeight: designWeights.medium }} />;
      })
    }
    renderInput={(params) => (
      <Mui.TextField {...params} placeholder={value.length ? "" : placeholder} error={Boolean(error)} sx={globalTextFieldSx} />
    )}
  />
);

// Formula shown as a grey code chip in tables.
export const FormulaChip = ({ formula }) => (
  <Mui.Box
    component="span"
    title={formula}
    sx={{
      display: "inline-block",
      maxWidth: "100%",
      px: 1,
      py: 0.25,
      borderRadius: "6px",
      backgroundColor: designColors.slate100,
      color: designColors.slate700,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
      fontSize: 12.5,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      verticalAlign: "middle",
    }}
  >
    {formula}
  </Mui.Box>
);

// Horizontal milestones in a drawer card (member History, challan Status):
// done steps get a teal check, the rest a grey ring.
// steps: [{ label, date, done }]
export const MilestoneStepper = ({ title, steps }) => (
  <Mui.Box sx={{ p: 2, border: `1px solid ${designColors.slate200}`, borderRadius: 2, backgroundColor: designColors.white }}>
    {title ? <Mui.Typography sx={{ ...designType.sectionHeading, mb: 2 }}>{title}</Mui.Typography> : null}
    <Mui.Box sx={{ display: "grid", gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
      {steps.map((step, index) => (
        <Mui.Box key={step.label} sx={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", px: 0.5 }}>
          {index > 0 ? (
            <Mui.Box
              sx={{
                position: "absolute",
                top: 11,
                left: 0,
                right: "calc(50% + 16px)",
                height: 2,
                backgroundColor: step.done ? designColors.teal : designColors.slate300,
              }}
            />
          ) : null}
          {index < steps.length - 1 ? (
            <Mui.Box
              sx={{
                position: "absolute",
                top: 11,
                right: 0,
                left: "calc(50% + 16px)",
                height: 2,
                backgroundColor: steps[index + 1].done ? designColors.teal : designColors.slate300,
              }}
            />
          ) : null}
          <Mui.Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: step.done ? designColors.teal : designColors.white,
              border: step.done ? "none" : `2px solid ${designColors.slate300}`,
              color: designColors.white,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {step.done ? "✓" : ""}
          </Mui.Box>
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, mt: 1 }}>{step.label}</Mui.Typography>
          <Mui.Typography sx={designType.captionText}>{step.date || "–"}</Mui.Typography>
        </Mui.Box>
      ))}
    </Mui.Box>
  </Mui.Box>
);

// Initials for avatars ("Ravi Kumar" -> "RK").
export const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "–";

export const formatInr = (value) =>
  value === "" || value == null || !Number.isFinite(Number(value)) ? "–" : Number(value).toLocaleString("en-IN");

// Small labelled dropdown for page filters (Payroll Month, Period, Code ...).
export const FilterSelect = ({ label, value, options, onChange }) => (
  <Mui.Box sx={{ width: { xs: "100%", sm: 220 } }}>
    <Mui.Typography sx={{ ...designType.captionText, mb: 0.5 }}>{label}</Mui.Typography>
    <SelectField value={value} onChange={(next) => next !== "" && next != null && onChange(next)} options={options} />
  </Mui.Box>
);
